import React from 'react';
import {
  MicIcon,
  SectionTemplatesIcon,
} from 'core/icons';
import ToolTip from 'core/components/Tooltip';
import Button from 'pages/Dashboard/components/Button';
import PatientHeader from 'pages/Dashboard/components/PatientHeader';
import RefreshBanner from 'pages/Dashboard/components/RefreshBanner';
import {
  useSnapActions,
  useSnapMode,
  useSnapVisitDetails,
} from 'views/EMR/stores/snap';
import RecordingIndicator from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/MainContent/RecordingIndicator';
import ExtendedVisitNoteSalt from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/VisitNoteSalt';
import VisitNoteMasterTemplateButton from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/VisitNoteMasterTemplateButton';
import PCC from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC';
import {
  useVisitNoteFormBridge,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/contexts/visitNoteFormBridge';
import {
  Patient,
} from 'pages/Dashboard/types/patient';
import {
  MasterTemplateDTO,
} from 'dtos';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import SnapMobileAppIndicator from 'views/EMR/components/Snap/SnapMobileAppIndicator';

type Props = {
  patient: Patient;
  encounterId: number;
  visitNoteId: number;
  patientId: number;
  visitDateTime?: string;
  editable: boolean;
  savingVisitNote: boolean;
  onClose: () => void;
  onRefreshVisitNote: () => void;
  isRefreshingVisitNote: boolean;
  onApplyMasterTemplate: (template: MasterTemplateDTO) => void;
  onOpenBulkSectionTemplates: () => void;
  onBeforePCCEnroll: () => Promise<boolean>;
};

export default function VisitNoteHeader({
  patient,
  encounterId,
  visitNoteId,
  patientId,
  visitDateTime,
  editable,
  savingVisitNote,
  onClose,
  onRefreshVisitNote,
  isRefreshingVisitNote,
  onApplyMasterTemplate,
  onOpenBulkSectionTemplates,
  onBeforePCCEnroll,
}: Props) {
  const { openSnap } = useSnapActions();
  const visitDetails = useSnapVisitDetails();
  const snapMode = useSnapMode();
  const { needsRefresh } = useVisitNoteFormBridge();

  const isSnapInProgress = snapMode !== 'closed';
  const snapTooltip = visitDetails?.encounterId !== encounterId
    ? (
      <>
        <span>SNAP in progress for another encounter with</span>
        <br />
        <span className="font-semibold">{visitDetails?.patientName}</span>
      </>
    )
    : 'SNAP in progress';

  return (
    <div className="flex w-full justify-between border-b border-b-gray-300">
      <div className="flex flex-col w-full">
        <PatientHeader
          className="!border-b-0"
          patient={patient}
          onClose={onClose}
        />
        <div className="flex justify-between">
          {editable && (
            <div className="flex gap-2 items-center flex-wrap w-fit px-4 pb-4">
              <div className="inline-flex items-stretch h-6 rounded border border-slate-200 overflow-hidden">
                <ToolTip
                  title={snapTooltip}
                  hide={!isSnapInProgress}
                >
                  <span className="flex">
                    <Button
                      className="!px-2.5 !py-0.5 !h-full !border-0 !rounded-none hover:!bg-gray-50"
                      variant="outlined"
                      disabled={isSnapInProgress}
                      onClick={(event) => {
                        openSnap(event.currentTarget, {
                          encounterId,
                          patientId,
                          patientName: getDisplayName(patient?.formattedNames),
                          visitDateTime: visitDateTime ?? '',
                        });
                      }}
                    >
                      <MicIcon className="!w-4 !h-4 !mr-1.5 !text-inherit" />
                      SNAP
                      {isSnapInProgress && <RecordingIndicator />}
                    </Button>
                  </span>
                </ToolTip>
                <SnapMobileAppIndicator />
              </div>
              <ExtendedVisitNoteSalt
                visitNoteId={visitNoteId}
                patientId={patientId}
                disabled={savingVisitNote}
              />
              <VisitNoteMasterTemplateButton
                disabled={savingVisitNote}
                onApplyTemplate={onApplyMasterTemplate}
              />
              <Button
                className="!border-secondary !px-2.5 !py-0.5"
                variant="outlined"
                color="primary"
                type="button"
                disabled={savingVisitNote}
                onClick={onOpenBulkSectionTemplates}
              >
                <SectionTemplatesIcon className="!mr-1.5 !h-4 !w-4" />
                Use Section Templates
              </Button>
            </div>
          )}
          <div className="flex gap-2 items-center flex-wrap w-fit px-4 pb-4 ml-auto">
            <PCC
              patient={patient}
              patientId={patientId}
              encounterId={encounterId}
              visitNoteId={visitNoteId}
              onBeforeEnroll={onBeforePCCEnroll}
            />
          </div>
        </div>
        {needsRefresh && (
          <RefreshBanner
            message="This visit note was updated elsewhere."
            disabled={isRefreshingVisitNote}
            onRefresh={onRefreshVisitNote}
          />
        )}
      </div>
    </div>
  );
}
